import { FC, useRef } from "react";
import {
    Flex,
    Heading,
    HorizontalScroll,
    Box,
    Button,
    Icon,
    Text,
} from "@smile2impress/components/relaunch";
import {
    useBreakpointValue,
    ArrowLeftCircledIcon,
    ArrowRightCircledIcon,
} from "@smile2impress/components";

import {
    TrustpilotWidget,
    TrustpilotWidgetVariant,
} from "components/TrustpilotWidget";
import { CtaButton } from "components/CtaButton";

import { SliderProps } from "./Slider.types";
import {
    CustomerReview,
    CustomerReviewProps,
} from "../primitives/CustomerReview";

export const Slider: FC<SliderProps> = ({ content, headingAs }) => {
    const { title, items, cta, trustpilotLink, disclaimer } = content;
    const isMobile = useBreakpointValue({
        base: true,
        tablet: true,
        desktop: false,
    });
    const sliderRef = useRef<HTMLDivElement | null>(null);

    const slideStep = 500;

    const slideLeft = () => {
        sliderRef?.current?.scrollTo({
            top: 0,
            left: sliderRef?.current?.scrollLeft - slideStep,
            behavior: "smooth",
        });
    };

    const slideRight = () => {
        sliderRef?.current?.scrollTo({
            top: 0,
            left: sliderRef?.current?.scrollLeft + slideStep,
            behavior: "smooth",
        });
    };

    const getItemWidth = (item: CustomerReviewProps) => {
        if (item.text && item.text.length > 250) return "1000px";
        else if (item.text && item.text.length > 180) return "700px";
        return "500px";
    };

    return (
        <Flex direction="column" mx={[-2, -2, -2, -2]}>
            <Box px={[2, 2, 2, 2]}>
                <Flex
                    mb={[3, 3, 4]}
                    px={[0, 0, 4]}
                    justifyContent="space-between"
                    alignItems="center"
                >
                    <Heading
                        textStyle={["title1"]}
                        color="text.base"
                        as={headingAs}
                        maxW={["100%", "100%", "65%"]}
                    >
                        {title}
                    </Heading>
                    {!isMobile && (
                        <Flex alignItems="center" columnGap={[0.7]}>
                            {trustpilotLink && (
                                <TrustpilotWidget
                                    link={trustpilotLink}
                                    variant={{
                                        base: TrustpilotWidgetVariant.MICRO_TRUST_SCORE,
                                        desktop:
                                            TrustpilotWidgetVariant.MICRO_TRUST_SCORE,
                                    }}
                                    mr={[0.7]}
                                />
                            )}

                            <Button
                                variant="ghost"
                                px={0}
                                py={0}
                                onClick={slideLeft}
                            >
                                <Icon
                                    w={3}
                                    h={3}
                                    as={ArrowLeftCircledIcon}
                                    color="base.gunmetal"
                                />
                            </Button>
                            {cta && <CtaButton {...cta} />}
                            <Button
                                variant="ghost"
                                px={0}
                                py={0}
                                onClick={slideRight}
                            >
                                <Icon
                                    w={3}
                                    h={3}
                                    as={ArrowRightCircledIcon}
                                    color="base.gunmetal"
                                />
                            </Button>
                        </Flex>
                    )}
                </Flex>
            </Box>

            <HorizontalScroll ref={sliderRef}>
                <Box
                    whiteSpace={"nowrap"}
                    transition={"margin-left 0.3s ease-in-out"}
                >
                    {items.map((item, index) => (
                        <Box
                            minWidth={[8, 8, 0]}
                            maxWidth={[0, 0, getItemWidth(item)]}
                            display="inline-block"
                            height={["420px", "420px", "450px"]}
                            key={index}
                            mr={2}
                            _first={{
                                ml: [2, 2, 4],
                                pl: [0, 0, 2],
                            }}
                            _last={{
                                mr: [2, 2, 4],
                                pr: [0, 0, 2],
                            }}
                            alignSelf="stretch"
                        >
                            <CustomerReview {...item} />
                        </Box>
                    ))}
                </Box>
            </HorizontalScroll>

            {!isMobile && disclaimer && (
                <Box
                    mt={[2, 2, 3]}
                    px={[2, 2, 2, 2]}
                    mx={[0, 0, 4]}
                    textAlign={["center", "center", "left"]}
                >
                    <Text textStyle={["captionSmall"]} color="base.halfBlack">
                        {disclaimer}
                    </Text>
                </Box>
            )}

            {isMobile && (
                <>
                    <Flex px={1} mt={[1, 1, 0]}>
                        {trustpilotLink && (
                            <TrustpilotWidget
                                link={trustpilotLink}
                                variant={{
                                    base: TrustpilotWidgetVariant.MICRO_TRUST_SCORE,
                                    desktop:
                                        TrustpilotWidgetVariant.MICRO_TRUST_SCORE,
                                }}
                            />
                        )}
                    </Flex>
                    {disclaimer && (
                        <Box
                            mt={[2, 2, 3]}
                            px={[2, 2, 2]}
                            mb={cta ? [-1, -1, 0] : 0}
                            textAlign={["center", "center", "left"]}
                        >
                            <Text
                                textStyle={["captionSmall"]}
                                color="base.halfBlack"
                            >
                                {disclaimer}
                            </Text>
                        </Box>
                    )}
                    {cta && (
                        <Box px={[2, 2, 0]} mt={[1, 1, 0]}>
                            <CtaButton {...cta} />
                        </Box>
                    )}
                </>
            )}
        </Flex>
    );
};
